<template>
  <BillingSkeletonLoader v-if="loading || isInitializing" />
  <div v-else class="proxima_nova">
    <BillingTrialHeader v-if="status === 'FREE_TRIAL'" :trialDays="trialDays" />

    <div id="manage-billing-v3" class="flex flex-col gap-12 proxima_nova max-w-5xl mx-auto px-4 md:px-8 py-2 pb-28" :class="{ 'pt-16': status === 'FREE_TRIAL' }">
      <div class="flex flex-col gap-6 items-center">
        <div class="flex flex-col gap-1 items-center">
          <span class="text-[30px] font-semibold text_color">Upgrade your plan</span>
          <span class="text-[20px] secondary_color"> Pricing for one-person startups to Fortune 500 enterprises. </span>
        </div>
        <div class="flex flex-col items-center">
          <!-- TabComponent removed -->
        </div>
      </div>
      <div class="flex flex-col gap-6">
        <!-- 1. Payment Details -->
        <div class="flex flex-col gap-4">
          <span class="text-[24px] text_color font-semibold">
            {{ billingAccount?.default_payment_method ? '1. Payment Details' : '1. Add Payment Details' }}
          </span>

          <!-- Show saved payment method -->
          <div v-if="billingAccount?.default_payment_method && !showChangePaymentForm" class="flex flex-col gap-4">
            <div class="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <ThemeImage
                  :icon="
                    billingAccount.default_payment_method_brand === 'visa'
                      ? imageNames.VISA_ICON
                      : billingAccount.default_payment_method_brand === 'mastercard'
                        ? imageNames.MASTERCARD_ICON
                        : billingAccount.default_payment_method_brand === 'amex'
                          ? imageNames.AMEX_ICON
                          : billingAccount.default_payment_method_brand === 'discover'
                            ? imageNames.DISCOVER_ICON
                            : imageNames.UNKNOWN_ICON
                  "
                  class="w-12 h-8"
                />
                <div class="flex flex-col">
                  <span class="text-sm text-gray-600">Card ending in</span>
                  <span class="text-sm text-gray-600">•••• •••• •••• {{ billingAccount.default_payment_method }}</span>
                </div>
              </div>
              <a-button class="secondary_medium" @click="showChangePaymentForm = true"> Change Payment Method </a-button>
            </div>
          </div>

          <!-- Show payment form (for new or changing payment method) -->
          <div v-if="!billingAccount?.default_payment_method || showChangePaymentForm">
            <StripePaymentMethodForm @success="handlePaymentMethodSuccess" @error="handlePaymentMethodError" />
            <!-- <a-button v-if="showChangePaymentForm" class="mt-2 secondary_medium" @click="showChangePaymentForm = false"> Cancel </a-button> -->
          </div>
        </div>

        <!-- 2. Select Billing Cycle -->
        <div class="flex flex-col gap-4">
          <span class="text-[24px] text_color font-semibold">2. Select Billing Cycle</span>
          <div class="flex flex-col items-center">
            <TabComponent :tabs="filteredBillingTabs" :selectedTab="selectedTab" @tab-change="selectBillingPeriod" />
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <span class="text-[24px] text_color font-semibold">3. Add Credits</span>
          <WhatAreCreditsComponent />
          <CreditsSlider
            ref="creditsSliderRef"
            :billingPeriod="selectedTab"
            :currentSelectedPack="initialCreditAddons"
            :creditPacks="creditPacksData[billingPeriodMap[selectedTab]] || []"
            :loading="loadingCredits"
            @pack-selected="handleCreditPackSelected"
          />
        </div>

        <!-- 3. Manage Paid Apps -->
        <div class="flex flex-col gap-4">
          <span class="text-[24px] text_color font-semibold">4. Apps</span>
          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr class="bg-gray-50 text-gray-500 text-xs uppercase">
                  <th class="p-4 font-medium">App name</th>
                  <th class="p-4 font-medium">Billing amount</th>
                  <th class="p-4 font-medium">Number of users</th>
                  <th class="p-4 font-medium">Amount per month</th>
                  <th class="p-4"></th>
                </tr>
              </thead>
              <tbody class="text-sm">
                <tr v-for="sub in subscriptionsWithApps" :key="sub.id" class="border-b border-gray-100">
                  <td class="p-4 flex items-center gap-3">
                    <!-- Placeholder icon -->
                    <div class="w-8 h-8 bg-gray-100 rounded flex items-center justify-center" v-if="sub.app_image_url">
                      <img :src="sub.app_image_url" alt="App logo" class="w-full h-full object-contain" />
                    </div>
                    <div class="w-8 h-8 bg-gray-100 rounded flex items-center justify-center" v-else>
                      <span class="text-xs font-bold">{{ sub.app_title?.substring(0, 2).toUpperCase() }}</span>
                    </div>
                    <span class="font-medium text-gray-900">{{ sub.app_title }}</span>
                  </td>
                  <td class="p-4">
                    <div class="flex flex-col" v-if="sub.current_period_plan?.pricing_model === 'per_seat'">
                      <span class="font-bold text-gray-900">${{ sub.current_period_plan?.price_per_seat }}</span>
                      <span class="text-xs text-gray-500">/user/{{ getBillingPeriodDisplayText(sub.current_period_plan?.billing_period) }}</span>
                    </div>
                    <div class="flex flex-col" v-else-if="sub.current_period_plan?.pricing_model === 'fixed'">
                      <span class="font-bold text-gray-900">${{ sub.current_period_plan?.price_per_seat }}</span>
                      <span class="text-xs text-gray-500">/{{ getBillingPeriodDisplayText(sub.current_period_plan?.billing_period) }}</span>
                    </div>
                    <div class="flex flex-col" v-else-if="sub.current_period_plan?.pricing_model === 'free'">
                      <span class="font-bold text-gray-900">Free</span>
                    </div>
                  </td>
                  <td class="p-4">
                    <span class="font-bold text-gray-900">{{ sub.seat_count }}</span>
                  </td>
                  <td class="p-4">
                    <div class="flex flex-col" v-if="sub.current_period_plan?.pricing_model === 'fixed'">
                      <span class="font-bold text-gray-900">${{ sub.current_period_plan?.monthly_price }}</span>
                      <span class="text-xs text-gray-500">/month</span>
                    </div>
                    <div class="flex flex-col" v-else-if="sub.current_period_plan?.pricing_model === 'per_seat'">
                      <span class="font-bold text-gray-900">${{ sub.current_period_plan?.monthly_price }}</span>
                      <span class="text-xs text-gray-500">/user/month</span>
                    </div>
                    <div class="flex flex-col" v-else-if="sub.current_period_plan?.pricing_model === 'free'">
                      <span class="font-bold text-gray-900">Free</span>
                    </div>
                  </td>
                  <td class="p-4 text-right">
                    <button class="text-gray-400 hover:text-gray-600">
                      <ThemeImage :icon="imageNames.MORE_ICON_VERTICAL" class="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                <tr v-if="subscriptions.length === 0">
                  <td colspan="5" class="p-4 text-center text-gray-500">No active subscriptions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-6 items-center">
        <span class="text-[24px] font-semibold text_color">Trusted by forwarding businesses of all sizes</span>
        <div class="flex flex-row gap-8 items-center w-full">
          <img :src="companyLogoSrc" alt="Company logos" class="w-full object-contain" />
        </div>
      </div>
      <div class="flex flex-col gap-6 items-center">
        <span class="text-[24px] font-semibold text_color">Real Customers, Real Impact</span>
        <div class="flex flex-row gap-6">
          <CustomReviewComponent
            v-for="(review, idx) in customerReviews"
            :key="idx"
            :review="review.review"
            :name="review.name"
            :userName="review.userName"
            :designation="review.designation"
            :avatar="review.avatar"
          />
        </div>
      </div>
      <div class="flex flex-col gap-6 w-full">
        <span class="text-[24px] font-semibold text_color text-center">Know Your Billing</span>
        <BillingFAQAccordion :faqList="billingFAQ" />
      </div>
    </div>

    <!-- Show modals and footer only when not loading -->
    <div v-if="!loading && !isInitializing">
      <CustomModal v-model:visible="showCancelTrialModal" title="Cancel Trial and Downgrade to Free Plan" width="500px">
        <div class="flex flex-col items-start gap-4">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.warningIcon" alt="warningIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2">
              You still have <span class="font-semibold text_color">{{ trialDays }} days</span> of Advanced features left. Cancelling your trial will immediately downgrade you to the free plan, remove
              all trial credits and you'll lose access to all advanced features. Are you sure you want to cancel your free trial?
            </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="confirmCancelTrial" :loading="isModalProcessing" :disabled="isModalProcessing">
              {{ isModalProcessing ? 'Canceling...' : 'Cancel Trial' }}
            </a-button>
            <a-button class="primary_medium" @click="cancelTrialModalClose">Keep Trial</a-button>
          </div>
        </template>
      </CustomModal>

      <CustomModal v-model:visible="showPaymentDuesModal" title="Payment Required" width="500px">
        <div class="flex flex-col items-start gap-4">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.warningIcon" alt="warningIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2"> Please clear the pending dues to make changes to your plan </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="closePaymentDuesModal">Cancel</a-button>
            <a-button class="primary_medium" @click="handlePayNow">Pay Now</a-button>
          </div>
        </template>
      </CustomModal>

      <CustomModal v-model:visible="showContactSalesModal" title="Talk to Sales for Plan Change" width="500px">
        <div class="flex flex-col items-start">
          <div class="flex flex-row justify-center items-start">
            <img :src="imagePath.warningIcon" alt="warningIcon" class="size-6" />
            <span class="secondary_color text-left text-sm ml-2">
              Changing your billing cycle requires assistance from our Sales Team. Please schedule a call with us to discuss your requirements.
            </span>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-3">
            <a-button class="secondary_medium" @click="showContactSalesModal = false">Cancel</a-button>
            <a-button class="primary_medium" @click="handleContactSales">Talk to Sales</a-button>
          </div>
        </template>
      </CustomModal>
      <StripeCheckoutModal v-model:visible="showStripeCheckout" :client-secret="stripeClientSecret" :modal-title="checkoutModalTitle" @success="handleCheckoutSuccess" @error="handleCheckoutError" />
    </div>
  </div>
  <BillingFooter
    class="billing-footer-sticky"
    @setup-billing="setupBilling"
    @cancel-trial="handleCancelTrial"
    :billing-period="billingPeriod"
    :billed-monthly="calculatedTotal"
    :due-today="calculatedDue"
    :credit-packs="selectedCreditPack ? 1 : 0"
    :credit-packs-amount="calculatedCreditPackCost"
    :is-calculating-due="isCalculatingDue"
    :enable-button="enableButton"
    :selected-credit-pack="selectedCreditPack"
    :is-processing-checkout="isProcessingCheckout"
    :status="status"
    :trial-days="trialDays"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import ThemeImage from '@/components/custom/ThemeImage.vue';
import TabComponent from '@/components/v3/TabComponentV3.vue';
import { imageNames } from '@/javascript/imageConstants';
import { customerReviews, billingFAQ, billingTabsV3 } from '@/constants/billingConstants';
import { billingPeriodMap, toFrontendPeriod, getBillingPeriodDisplayText } from '@/utils/billingPeriodUtils';
import WhatAreCreditsComponent from '@/components/manage_billing_components/WhatAreCreditsComponent.vue';
import CreditsSlider from '@/components/v3/CreditsSliderV3.vue';
import CustomReviewComponent from '@/components/manage_billing_components/CustomReviewComponent.vue';
import BillingFAQAccordion from '@/components/manage_billing_components/BillingFAQAccordion.vue';
import BillingFooter from '@/components/v3/BillingFooterV3.vue';
import DashboardServiceV3 from '@/services/DashboardServiceV3';
import companyLogoSrc from '@/assets/images/company_logos.svg';
import { message } from 'ant-design-vue';
import CustomModal from '@/components/custom/CustomModal.vue';
import StripeCheckoutModal from '@/components/manage_billing_components/StripeCheckoutModal.vue';
import StripePaymentMethodForm from '@/components/manage_billing_components/StripePaymentMethodForm.vue';
import imagePath from '@/javascript/imagePath';
import BillingTrialHeader from '@/components/manage_billing_components/BillingTrialHeader.vue';
import BillingSkeletonLoader from '@/components/custom/BillingSkeletonLoader.vue';

// State management
const loading = ref(false);
const selectedTab = ref('annual');
const selectedCreditPack = ref(null);
const creditPacksData = ref({});
const loadingCredits = ref(false);
const trialDays = ref(0);
const initialCreditAddons = ref(0);

// Billing state
const status = ref('');
const billingPeriod = ref('annual');
const originalBillingPeriod = ref('annual');
const billingAccount = ref(null);
const showChangePaymentForm = ref(false);

// Calculated due amounts
const calculatedDue = ref(0);
const calculatedTotal = ref(0);
const calculatedCurrentTotal = ref(0);
const calculatedCreditPackCost = ref(0);
const calculatedAppSubscriptionCost = ref(0);
const calculatedTotalCredits = ref(0);
const isCalculatingDue = ref(false);

const showStripeCheckout = ref(false);
const stripeClientSecret = ref('');
const checkoutModalTitle = ref('Complete Your Payment');
const isProcessingCheckout = ref(false);

// Modal states
const showCancelTrialModal = ref(false);
const showPaymentDuesModal = ref(false);
const showContactSalesModal = ref(false);
const paymentRetryLink = ref('');
const isModalProcessing = ref(false);

const subscriptions = ref([]);
const apps = ref([]); // Apps from the app store

// subscriptionsWithApps: enhances each subscription with the corresponding app's image_url, title, and current period plan
const subscriptionsWithApps = computed(() => {
  if (!subscriptions.value.length || !apps.value.length) return subscriptions.value;

  const currentPeriod = billingPeriodMap[selectedTab.value] || 'monthly';

  return subscriptions.value.map((sub) => {
    const matchedApp = apps.value.find((app) => {
      // Try matching by app_id, id, or any available identifier
      if (sub.app_id && app.id) {
        return app.id === sub.app_id;
      }
      if (sub.app_code && app.app_code) {
        return app.app_code === sub.app_code;
      }
      return false;
    });

    // Get the plan for the current selected billing period
    let currentPeriodPlan = sub.plan_version;

    // For FREE subscriptions (status is 'free' or plan is free), use the current plan_version
    // which should be the free plan. Don't look up from the plans map which contains paid plans.
    const isFreeSubscription = sub.status === 'free' || sub.plan_version?.pricing_model === 'free';

    if (!isFreeSubscription && sub.plans && sub.plans[currentPeriod]) {
      // Only use paid plan from plans map for non-free subscriptions
      currentPeriodPlan = sub.plans[currentPeriod];
    } else if (isFreeSubscription) {
      // For free subscriptions, try to find the free plan for the current period
      if (sub.free_plan_versions && sub.free_plan_versions.length > 0) {
        const freePlanForPeriod = sub.free_plan_versions.find((fp) => fp.billing_period === currentPeriod);
        if (freePlanForPeriod) {
          currentPeriodPlan = freePlanForPeriod;
        }
      }
    }

    return {
      ...sub,
      app_image_url: matchedApp?.app_logo_url || '', // fallback: empty string if not found
      app_title: matchedApp?.title || sub.app_name || '',
      current_period_plan: currentPeriodPlan, // Plan for the selected period
    };
  });
});

// Add state for enabled billing periods
const enabledBillingPeriods = ref({
  monthly: true,
  quarterly: true,
  semi_annual: false,
  annual: true,
  two_year: false,
});

// Filter billing tabs based on enabled periods
const filteredBillingTabs = computed(() => {
  return billingTabsV3.filter((tab) => {
    return enabledBillingPeriods.value[tab.value] === true;
  });
});

const hasChanges = computed(() => {
  let creditChanges = false;
  if (selectedCreditPack.value && initialCreditAddons.value) {
    creditChanges = selectedCreditPack.value.pack_size !== initialCreditAddons.value;
  } else if (!initialCreditAddons.value && selectedCreditPack.value) {
    creditChanges = selectedCreditPack.value.pack_size > 0;
  } else if (initialCreditAddons.value && !selectedCreditPack.value) {
    creditChanges = true;
  }

  const hasBillingPeriodChanges = originalBillingPeriod.value && effectiveBillingPeriod.value !== originalBillingPeriod.value;

  return creditChanges || hasBillingPeriodChanges;
});

const isDowngrade = computed(() => {
  // Only check for billing period downgrades - credit pack downgrades are allowed
  // Don't check downgrades for free trial users or free plan users
  if (status.value === 'FREE_TRIAL' || status.value === 'FREE_PLAN' || status.value === 'free') {
    return false;
  }

  // Check for billing period downgrades for paid users
  if (originalBillingPeriod.value && status.value === 'PAID') {
    const billingPeriodHierarchy = {
      two_year: 5,
      annual: 4,
      semi_annual: 3,
      quarterly: 2,
      monthly: 1,
    };

    const currentLevel = billingPeriodHierarchy[originalBillingPeriod.value] || 0;
    const selectedLevel = billingPeriodHierarchy[effectiveBillingPeriod.value] || 0;

    if (selectedLevel < currentLevel) {
      return true;
    }
  }

  // Credit pack downgrades are now allowed - don't check them
  return false;
});

const enableButton = computed(() => {
  // Disable if no changes
  // if (!hasChanges.value && calculatedDue.value === 0) {
  //   return false;
  // }

  // Disable if due today is 0 (no payment required)
  return true;
});

let isInitializing = ref(true);

const effectiveBillingPeriod = computed(() => {
  return billingPeriodMap[selectedTab.value] || 'monthly';
});

const fetchBillingOverview = async () => {
  console.log('ManageBillingV3: fetchBillingOverview start');
  loading.value = true;
  try {
    // Use V3 endpoint - getBillingSummary
    console.log('ManageBillingV3: fetching summary and account');
    const [summaryResponse, accountResponse] = await Promise.all([DashboardServiceV3.getBillingSummary(), DashboardServiceV3.getOrCreateBillingAccount()]);
    console.log('ManageBillingV3: summary and account fetched', { summaryResponse, accountResponse });

    if (accountResponse?.success) {
      billingAccount.value = accountResponse.account;
      console.log('ManageBillingV3: billingAccount set', billingAccount.value);

      // Set initial credit pack selection from account
      if (billingAccount.value?.credit_pack_selected) {
        initialCreditAddons.value = billingAccount.value.credit_pack_selected;
        console.log('ManageBillingV3: initialCreditAddons set to', initialCreditAddons.value);
      }

      // Get billing period from account
      if (billingAccount.value?.billing_period) {
        billingPeriod.value = billingAccount.value.billing_period;
        selectedTab.value = toFrontendPeriod(billingAccount.value.billing_period);
        originalBillingPeriod.value = billingAccount.value.billing_period;
      }
    }

    const response = summaryResponse;
    if (response?.success && response.data) {
      const summary = response.data;
      status.value = summary.status || 'FREE_PLAN';
      trialDays.value = summary.trial_days_left || 0;

      // Get subscriptions
      console.log('ManageBillingV3: fetching subscriptions');
      const subsResponse = await DashboardServiceV3.getAppSubscriptions();
      console.log('ManageBillingV3: subscriptions fetched', subsResponse);

      if (subsResponse?.success && subsResponse.subscriptions) {
        subscriptions.value = subsResponse.subscriptions;
      }
    }
  } catch (error) {
    console.error('Error fetching billing overview:', error);
  } finally {
    loading.value = false;
    console.log('ManageBillingV3: loading set to false');
  }
};

const fetchCreditPacks = async () => {
  loadingCredits.value = true;
  try {
    const response = await DashboardServiceV3.getCreditPacks();
    if (response?.success && response.credit_packs) {
      const data = response.credit_packs;
      ['annual', 'monthly', 'quarterly', 'semi_annual', 'two_year'].forEach((period) => {
        if (data[period] && data[period].length > 0) {
          if (data[period][0].pack_size !== 0) {
            data[period].unshift({
              pack_size: 0,
              pack_price: 0,
              billing_period: period,
            });
          }
          data[period].push({
            pack_size: -1,
            pack_price: -1,
            billing_period: period,
            isCustom: true,
          });
        }
      });
      creditPacksData.value = data;
      console.log('Credit packs fetched:', creditPacksData.value);
    } else {
      creditPacksData.value = {
        annual: [],
        monthly: [],
        quarterly: [],
        semi_annual: [],
        two_year: [],
      };
    }
  } catch (error) {
    console.error('Error fetching credit packs:', error);
    creditPacksData.value = {
      annual: [],
      monthly: [],
      quarterly: [],
      semi_annual: [],
      two_year: [],
    };
  } finally {
    loadingCredits.value = false;
  }
};

const calculateDueAmount = async () => {
  if (loading.value || isInitializing.value) {
    return;
  }

  isCalculatingDue.value = true;
  console.log('ManageBillingV3: isCalculatingDue set to true');

  try {
    const backendPeriod = billingPeriodMap[selectedTab.value] || 'monthly';
    const packSize = selectedCreditPack.value?.pack_size || 0;
    console.log('ManageBillingV3: calculating due amount', { backendPeriod, packSize }, { selectedCreditPack: selectedCreditPack.value });

    const response = await DashboardServiceV3.calculateDue(backendPeriod, packSize);

    if (response?.success && response.data) {
      calculatedDue.value = response.data.due_today || 0;
      calculatedTotal.value = response.data.total_cost || 0;
      calculatedCurrentTotal.value = response.data.current_total || 0;
      calculatedCreditPackCost.value = response.data.credit_pack_cost || 0;
      calculatedAppSubscriptionCost.value = response.data.app_subscription_cost || 0;
      calculatedTotalCredits.value = response.data.total_credits || 0;
    } else {
      // Fallback to simple calculation
      calculatedCreditPackCost.value = selectedCreditPack.value?.pack_price || 0;
      calculatedDue.value = calculatedCreditPackCost.value;
      calculatedTotal.value = calculatedCreditPackCost.value;
      calculatedAppSubscriptionCost.value = 0;
      calculatedTotalCredits.value = selectedCreditPack.value?.pack_size || 0;
    }
  } catch (error) {
    console.error('Error calculating due:', error);
    // Fallback to simple calculation
    calculatedCreditPackCost.value = selectedCreditPack.value?.pack_price || 0;
    calculatedDue.value = calculatedCreditPackCost.value;
    calculatedTotal.value = calculatedCreditPackCost.value;
    calculatedAppSubscriptionCost.value = 0;
    calculatedTotalCredits.value = selectedCreditPack.value?.pack_size || 0;
  } finally {
    isCalculatingDue.value = false;
    console.log('ManageBillingV3: isCalculatingDue set to false');
  }
};

// Event handlers
const selectBillingPeriod = (period) => {
  const newBillingPeriod = billingPeriodMap[period] || 'monthly';

  console.log('ManageBillingV3: selectBillingPeriod', {
    status: status.value,
    newBillingPeriod,
    originalBillingPeriod: originalBillingPeriod.value,
  });

  // Check for billing period DOWNGRADES for PAID users - upgrades are allowed
  if (originalBillingPeriod.value && status.value !== 'FREE_PLAN' && status.value !== 'free' && status.value !== 'FREE_TRIAL') {
    const billingPeriodHierarchy = {
      two_year: 5,
      annual: 4,
      semi_annual: 3,
      quarterly: 2,
      monthly: 1,
    };

    const currentLevel = billingPeriodHierarchy[originalBillingPeriod.value] || 0;
    const newLevel = billingPeriodHierarchy[newBillingPeriod] || 0;

    // Block only downgrades (newLevel < currentLevel), allow upgrades
    if (newLevel < currentLevel) {
      showContactSalesModal.value = true;
      return;
    }
  }

  selectedTab.value = period;
  billingPeriod.value = newBillingPeriod;
};

const handleCreditPackSelected = async (packSize) => {
  if (!packSize || packSize === 0) {
    selectedCreditPack.value = null;
    return;
  }

  // Find the pack from creditPacksData using billingPeriodMap
  const backendPeriod = billingPeriodMap[selectedTab.value] || 'monthly';
  const currentPacks = creditPacksData.value[backendPeriod] || [];
  const pack = currentPacks.find((p) => p.pack_size === packSize);

  if (!pack) {
    selectedCreditPack.value = null;
    return;
  }

  if (pack.isCustom) {
    selectedCreditPack.value = {
      pack_size: -1,
      pack_price: -1,
      isCustom: true,
    };
  } else {
    selectedCreditPack.value = {
      pack_size: pack.pack_size,
      pack_price: pack.pack_price,
    };
  }
  await calculateDueAmount();
};

const openPaymentRetryModal = (link) => {
  paymentRetryLink.value = link;
  showPaymentDuesModal.value = true;
};

const handlePayNow = () => {
  if (paymentRetryLink.value) {
    window.open(paymentRetryLink.value, '_blank');
  }
  showPaymentDuesModal.value = false;
};

const closePaymentDuesModal = () => {
  showPaymentDuesModal.value = false;
  paymentRetryLink.value = '';
};

const handleContactSales = () => {
  showContactSalesModal.value = false;
  window.open('https://meetings.superagi.com/Sales-Demo-Team', '_blank');
};

const setupBilling = async () => {
  try {
    // Check for billing period downgrade (credit pack downgrades are allowed)
    if (isDowngrade.value) {
      showContactSalesModal.value = true;
      return;
    }

    isProcessingCheckout.value = true;

    await nextTick();

    let currentCreditAddons = 0;
    if (selectedCreditPack.value && selectedCreditPack.value.pack_size !== undefined) {
      currentCreditAddons = selectedCreditPack.value.pack_size;
    }

    // Use V3 checkout endpoint
    const updateResponse = await DashboardServiceV3.updateBilling(billingPeriod.value, currentCreditAddons);

    if (updateResponse?.success) {
      const updateData = updateResponse.data;
      if (updateData?.requires_checkout) {
        showStripeCheckout.value = true;
        stripeClientSecret.value = updateData.checkout_session_secret;
        checkoutModalTitle.value = 'Complete Your Payment';
      } else {
        message.success('Billing updated successfully!');

        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    } else if (updateResponse?.contact_sales) {
      // Handle downgrade case - show contact sales modal
      isProcessingCheckout.value = false;
      isModalProcessing.value = false;
      showContactSalesModal.value = true;
    } else {
      // Handle other error cases
      isProcessingCheckout.value = false;
      isModalProcessing.value = false;
      message.error(updateResponse?.message || 'Error updating billing');
    }
  } catch (error) {
    isProcessingCheckout.value = false;
    isModalProcessing.value = false;

    // Check if the error response indicates a downgrade
    if (error?.response?.data?.contact_sales) {
      showContactSalesModal.value = true;
    } else {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || '';
      message.error(errorMessage || 'Error updating billing');
      console.error('Error:', error);
    }
  }
};
const handleCheckoutSuccess = async () => {
  showStripeCheckout.value = false;

  loading.value = true;
  try {
    await sleep(3000); // Wait for 3 seconds to ensure the checkout session is created
    await fetchBillingOverview();
    await fetchCreditPacks();

    message.success('Payment successful! Your billing has been updated.');
  } catch (error) {
    console.error('Error refreshing billing data:', error);
    message.error('Error updating billing information. Please refresh the page.');
  } finally {
    loading.value = false;
  }
};

const handleCheckoutError = (error) => {
  showStripeCheckout.value = false;
  stripeClientSecret.value = '';
  isProcessingCheckout.value = false;
  isModalProcessing.value = false;

  if (error?.message?.includes('cancel')) {
    message.info('Payment cancelled');
  } else {
    message.error('Payment failed. Please try again.');
  }
  console.error('Checkout error:', error);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const handlePaymentMethodSuccess = async () => {
  showChangePaymentForm.value = false; // Hide the form after successful update
  loading.value = true;
  // Notify opener window that payment method was added to avoid page reload
  if (window.opener) {
    window.opener.postMessage({ type: 'payment_method_added' }, '*');
  }
  try {
    await sleep(5000);
    // Reload billing account to verify payment method was saved
    await fetchBillingOverview();

    // Verify that the payment method was successfully saved
    if (billingAccount.value?.default_payment_method) {
      message.success('Payment method added successfully!');
      console.log('Payment method verified:', billingAccount.value.default_payment_method);
    } else {
      // If payment method is still not set, it might be a webhook delay
      message.warning('Payment method saved. It may take a moment to appear.');
      console.warn('Payment method not yet reflected in billing account');
    }
  } catch (error) {
    console.error('Error refreshing billing data:', error);
    message.error('Payment method saved but failed to refresh billing data. Please refresh the page.');
  } finally {
    loading.value = false;
  }
};

const handlePaymentMethodError = (error) => {
  console.error('Payment method error:', error);
  message.error(error?.message || 'Failed to add payment method');
};

const handleCancelTrial = () => {
  showCancelTrialModal.value = true;
};

const cancelTrialModalClose = () => {
  showCancelTrialModal.value = false;
};

const confirmCancelTrial = async () => {
  isModalProcessing.value = true;

  try {
    // V3 would have a similar endpoint
    message.success('Trial cancelled successfully!');
    showCancelTrialModal.value = false;

    setTimeout(() => {
      window.location.reload();
    }, 300);
  } catch (error) {
    console.error('Error canceling trial:', error);
    message.error('Error canceling trial. Please try again.');
    isModalProcessing.value = false;
  }
};

const fetchEnabledBillingPeriods = async () => {
  try {
    const periodsResponse = await DashboardServiceV3.getEnabledBillingPeriods();
    if (periodsResponse?.success && periodsResponse?.enabled_periods) {
      enabledBillingPeriods.value = periodsResponse.enabled_periods;

      // If current selected tab is not enabled, switch to the first enabled tab
      const isCurrentTabEnabled = enabledBillingPeriods.value[selectedTab.value] === true;
      if (filteredBillingTabs.value.length > 0 && !isCurrentTabEnabled) {
        selectedTab.value = filteredBillingTabs.value[0].value;
        billingPeriod.value = billingPeriodMap[selectedTab.value] || 'annual';
      }
    }
  } catch (error) {
    console.error('Error fetching enabled billing periods:', error);
  }
};

// Watchers
watch(
  [selectedCreditPack, selectedTab],
  async () => {
    if (!loading.value && !isInitializing.value) {
      await calculateDueAmount();
    }
  },
  { deep: true }
);

// Lifecycle
onMounted(async () => {
  console.log('ManageBillingV3: onMounted start');
  try {
    const initialWorkspaceId = localStorage.getItem('billing_workspace_id') || '';
    currentWorkspaceId.value = initialWorkspaceId;

    const appsResponse = await DashboardServiceV3.getApps();
    if (appsResponse?.success && appsResponse?.apps) {
      apps.value = appsResponse.apps;
    }

    console.log('ManageBillingV3: calling fetchBillingOverview');
    await fetchBillingOverview();
    console.log('ManageBillingV3: fetchBillingOverview done');

    console.log('ManageBillingV3: calling fetchEnabledBillingPeriods');
    await fetchEnabledBillingPeriods();
    console.log('ManageBillingV3: fetchEnabledBillingPeriods done');

    console.log('ManageBillingV3: calling fetchCreditPacks');
    await fetchCreditPacks();
    console.log('ManageBillingV3: fetchCreditPacks done');

    isInitializing.value = false;
    console.log('ManageBillingV3: isInitializing set to false');

    // Calculate initial due amount
    await calculateDueAmount();
  } catch (error) {
    console.error('Error initializing billing page:', error);
    message.error('Error loading billing information. Please refresh the page.');
    isInitializing.value = false;
  }
});

// Workspace tracking
const currentWorkspaceId = ref('');
const creditsSliderRef = ref(null);
</script>

<style scoped>
.plan-btn {
  width: fit-content;
  border-radius: 12px;
  padding: 7px 24px;
  font-size: 16px;
  font-weight: 600;
}
.plan-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.billing-footer-sticky {
  position: fixed;
  left: 225px;
  width: calc(100vw - 225px);
  bottom: 0;
  z-index: 10;
  height: 90px;
  margin-left: 0 !important;
}
</style>
